import { List, useTable } from '@refinedev/antd';
import { Table, Select, Space, Tag, Button, Input } from 'antd';
import { EyeOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

interface DriverTripBooking {
  id: number;
  seatsBooked: number;
  totalPrice: number;
  status: string;
  paymentMethod: string | null;
  isPaid: boolean;
  createdAt: string;
  passenger: {
    id: number;
    fullName: string;
    phone: string;
  };
  driverPostedTrip: {
    id: number;
    tripMode: string;
    departureAddress: string;
    destinationAddress: string;
    departureRegion: { nameUz: string } | null;
    destinationRegion: { nameUz: string } | null;
    driver: {
      fullName: string;
      phone: string;
    };
  };
}

const statusLabels: Record<string, string> = {
  PENDING: 'Kutilmoqda',
  CONFIRMED: 'Tasdiqlangan',
  CANCELLED: 'Bekor qilingan',
  COMPLETED: 'Yakunlangan',
  ON_THE_WAY: "Yo'lda",
  ARRIVED: 'Yetib keldi',
  IN_PROGRESS: 'Jarayonda',
};

const statusColors: Record<string, string> = {
  PENDING: 'gold',
  CONFIRMED: 'blue',
  CANCELLED: 'red',
  COMPLETED: 'green',
  ON_THE_WAY: 'orange',
  ARRIVED: 'cyan',
  IN_PROGRESS: 'purple',
};

const tripModeLabels: Record<string, string> = {
  SHAHARLAR_ARO: 'Shaharlar aro',
  VILOYATLAR_ARO: 'Viloyatlar aro',
};

export const DriverTripBookingList = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [searchFilter, setSearchFilter] = useState<string | undefined>(undefined);

  const { tableProps } = useTable<DriverTripBooking>({
    resource: 'admin/driver-trip-bookings',
    syncWithLocation: true,
    filters: {
      permanent: [
        ...(statusFilter ? [{ field: 'status', operator: 'eq' as const, value: statusFilter }] : []),
        ...(searchFilter ? [{ field: 'search', operator: 'eq' as const, value: searchFilter }] : []),
      ],
    },
  });

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('uz-UZ').format(price) + " so'm";
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('uz-UZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <List
      title="Sayohat buyurtmalari"
      headerButtons={() => null}
    >
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          placeholder="Holat"
          allowClear
          style={{ width: 180 }}
          value={statusFilter}
          onChange={(value) => setStatusFilter(value)}
          options={[
            { value: 'PENDING', label: 'Kutilmoqda' },
            { value: 'CONFIRMED', label: 'Tasdiqlangan' },
            { value: 'CANCELLED', label: 'Bekor qilingan' },
            { value: 'COMPLETED', label: 'Yakunlangan' },
          ]}
        />
        <Input.Search
          placeholder="Yo'lovchi nomi/telefoni"
          allowClear
          style={{ width: 250 }}
          onSearch={(value) => setSearchFilter(value || undefined)}
        />
      </Space>

      <Table {...tableProps} rowKey="id" size="small">
        <Table.Column dataIndex="id" title="ID" width={60} />
        <Table.Column
          title="Yo'lovchi"
          width={180}
          render={(_, record: DriverTripBooking) => (
            <div>
              <div style={{ fontWeight: 500 }}>{record.passenger?.fullName || '-'}</div>
              <div style={{ fontSize: 12, color: '#999' }}>{record.passenger?.phone || '-'}</div>
            </div>
          )}
        />
        <Table.Column
          title="Marshrut"
          render={(_, record: DriverTripBooking) => (
            <Space size={4}>
              <EnvironmentOutlined style={{ color: '#52c41a' }} />
              <span>{record.driverPostedTrip?.departureRegion?.nameUz || '-'}</span>
              <span style={{ color: '#999' }}>→</span>
              <EnvironmentOutlined style={{ color: '#f5222d' }} />
              <span>{record.driverPostedTrip?.destinationRegion?.nameUz || '-'}</span>
            </Space>
          )}
        />
        <Table.Column
          title="Rejim"
          width={120}
          render={(_, record: DriverTripBooking) => (
            <Tag color={record.driverPostedTrip?.tripMode === 'SHAHARLAR_ARO' ? 'blue' : 'purple'}>
              {tripModeLabels[record.driverPostedTrip?.tripMode] || '-'}
            </Tag>
          )}
        />
        <Table.Column
          title="Haydovchi"
          width={150}
          render={(_, record: DriverTripBooking) => (
            <div>
              <div style={{ fontWeight: 500 }}>{record.driverPostedTrip?.driver?.fullName || '-'}</div>
            </div>
          )}
        />
        <Table.Column dataIndex="seatsBooked" title="Joylar" width={70} />
        <Table.Column
          dataIndex="totalPrice"
          title="Summa"
          width={120}
          render={(price: number) => formatPrice(price)}
        />
        <Table.Column
          title="To'lov"
          width={110}
          render={(_, record: DriverTripBooking) => (
            <Space direction="vertical" size={0}>
              <span>{record.paymentMethod || '-'}</span>
              <Tag color={record.isPaid ? 'green' : 'orange'} style={{ fontSize: 11 }}>
                {record.isPaid ? "To'langan" : "To'lanmagan"}
              </Tag>
            </Space>
          )}
        />
        <Table.Column
          dataIndex="status"
          title="Holat"
          width={120}
          render={(status: string) => (
            <Tag color={statusColors[status] || 'default'}>
              {statusLabels[status] || status}
            </Tag>
          )}
        />
        <Table.Column
          dataIndex="createdAt"
          title="Sana"
          width={140}
          render={(date: string) => formatDate(date)}
        />
        <Table.Column
          title="Amallar"
          width={80}
          render={(_, record: DriverTripBooking) => (
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/driver-trip-bookings/${record.id}`)}
            />
          )}
        />
      </Table>
    </List>
  );
};
